import { isValidEmail } from 'services/Validation';
import { isEqual } from 'lodash';
import { appPhoneNumberToComponentPhoneNumberMapper } from 'services/Preferences';

export const getUpdatedFieldName = (field, hasResidential, mailingType) => {
  if (hasResidential) {
    return `${mailingType}${field}`;
  }

  return field;
};

export const shouldFieldValidate = (field, touchedFields) => {
  return touchedFields && touchedFields[field] === true;
};

export const phoneAndEmailFields = [
  'email',
  'daytimePhone',
  'daytimePhoneExt',
  'eveningPhone',
  'eveningPhoneExt',
  'mobilePhone',
  'faxNumber'
];

export const nonUSPhoneAndEmailFields = [
  'email',
  'daytimePhone',
  'daytimePhoneCountryCode',
  'eveningPhone',
  'eveningPhoneCountryCode',
  'mobilePhone',
  'mobilePhoneCountryCode',
  'faxNumber',
  'faxNumberCountryCode'
];

export const getRequiredFields = (addressType) => {
  if (addressType === 'us') {
    return ['addressOne', 'city', 'state', 'zipCode'];
  } else if (addressType === 'military') {
    return [
      'militaryAddressOne',
      'militaryCity',
      'militaryState',
      'militaryZipCode'
    ];
  } else if (addressType === 'non-us'){
    return ['nonUSAddressOne', 'nonUSCity', 'country', 'postalCode'];
  } else {
    return [];
  }
};

export const getValidFields = (addressType) => {
  if (addressType === 'us') {
    return [
      'addressOne',
      'addressTwo',
      'city',
      'state',
      'zipCode',
      'zipCodeExt'
    ];
  } else if (addressType === 'military') {
    return [
      'militaryAddressOne',
      'militaryAddressTwo',
      'militaryCity',
      'militaryState',
      'militaryZipCode'
    ];
  } else {
    return [
      'nonUSAddressOne',
      'nonUSAddressTwo',
      'nonUSCity',
      'country',
      'postalCode'
    ];
  }
};

export const getValidPhoneEmailFields = (addressType) => {
  if (addressType === 'non-us') {
    return nonUSPhoneAndEmailFields;
  } else{
    return phoneAndEmailFields
  }
};

export const isAddressSame = (stateAddress, storeAddress) => {
  const addressLineTwoKey =
    stateAddress.addressType === 'us'
      ? 'addressTwo'
      : stateAddress.addressType === 'military'
      ? 'militaryAddressTwo'
      : 'nonUSAddressTwo';
  const addressKeys = getRequiredFields(
    stateAddress && stateAddress.addressType
  );
  addressKeys.push(addressLineTwoKey);
  
  if(stateAddress.addressType === 'us'){
    addressKeys.push('zipCodeExt')
  }

  return addressKeys.every(
    (key) => stateAddress[key] === storeAddress[key]
  );
};

export const shouldDisableSave = (
  invalidFields,
  hasResidential,
  email,
  updatedPhoneNumbers,
  address = {},
  mailingAddress = {},
  primaryMailingAddress,
  residentialAddress,
  originalEmail,
  initialPhoneNumbers
) => {
  if (hasResidential) {
    const allMailingRequiredAddressFieldsNonEmpty = getRequiredFields(
      mailingAddress && mailingAddress.addressType
    ).every((field) => mailingAddress && mailingAddress[field] !== '');

    const allResidentialRequiredAddressFieldsNonEmpty = getRequiredFields(
      address && address.addressType
    ).every((field) => address && address[field] !== '');

    const allResidentialFieldsValid = !getValidFields(
      address && address.addressType
    ).some(
      (field) =>
        invalidFields &&
        invalidFields[
          getUpdatedFieldName(field, hasResidential, 'residential')
        ] &&
        (invalidFields[
          getUpdatedFieldName(field, hasResidential, 'residential')
        ].length ||
          Object.keys(
            invalidFields[
              getUpdatedFieldName(field, hasResidential, 'residential')
            ]
          ).length)
    );

    const allMailingFieldsValid = !getValidFields(
      mailingAddress && mailingAddress.addressType
    ).some(
      (field) =>
        invalidFields &&
        invalidFields[getUpdatedFieldName(field, hasResidential, 'mailing')] &&
        (invalidFields[getUpdatedFieldName(field, hasResidential, 'mailing')]
          .length ||
          Object.keys(
            invalidFields[getUpdatedFieldName(field, hasResidential, 'mailing')]
          ).length)
    );
    
    const allPhoneEmailFieldsValid = !getValidPhoneEmailFields(
      address & address.addressType
    ).some(
      (field) =>
        invalidFields &&
        invalidFields[field] &&
        (invalidFields[field].length ||
          Object.keys(invalidFields[field]).length)
    );

    const emailIsInvalid = !isValidEmail(email.email);

    const isBothAddressesSame =
      isAddressSame(mailingAddress, primaryMailingAddress) &&
      isAddressSame(address, residentialAddress);

    const isBothPhonesSame = isEqual(
      updatedPhoneNumbers,
      appPhoneNumberToComponentPhoneNumberMapper(initialPhoneNumbers)
    );
    
    const isBothEmailSame = isEqual(email.email, originalEmail.address);
    if(isBothAddressesSame && isBothPhonesSame && isBothEmailSame){
      return true;
    }
    
    return (
      !allResidentialRequiredAddressFieldsNonEmpty ||
      !allMailingRequiredAddressFieldsNonEmpty ||
      !allResidentialFieldsValid ||
      !allMailingFieldsValid ||
      !allPhoneEmailFieldsValid ||
      emailIsInvalid
    );
  } else {
    const { addressType } = address;
    const allRequiredAddressFieldsNonEmpty = getRequiredFields(
      addressType
    ).every((field) => address[field] !== '');
    const allFieldsValid = !getValidFields(addressType).some(
      (field) =>
        invalidFields &&
        invalidFields[field] &&
        (invalidFields[field].length ||
          Object.keys(invalidFields[field]).length)
    );
    
    const allPhoneEmailFieldsValid = !getValidPhoneEmailFields(
      addressType
    ).some(
      (field) =>
        invalidFields &&
        invalidFields[field] &&
        (invalidFields[field].length ||
          Object.keys(invalidFields[field]).length)
    );

    const emailIsInvalid = !isValidEmail(email.email);

    return (
      !allRequiredAddressFieldsNonEmpty ||
      !allFieldsValid ||
      !allPhoneEmailFieldsValid ||
      emailIsInvalid
    );
  }
};
